import { sql } from "drizzle-orm";
import { date, index, pgTable, timestamp, uniqueIndex, varchar } from "drizzle-orm/pg-core";
import { organisations, students, tutorProfiles, users } from "./schema.js";

/**
 * Earliest date covered by the Amelia history imported into the Hub.
 *
 * A student-specific payment-tracking date is an optional override. When no
 * override is stored, retrospective credit allocation uses this boundary so a
 * normal block can settle the student's oldest eligible unpaid lessons without
 * requiring an owner to configure every student first.
 */
export const DEFAULT_PAYMENT_TRACKING_AUTHORITATIVE_FROM = "2026-07-20";

/**
 * Optional per-student override for financially authoritative lesson history.
 *
 * Null means "use the default available-history boundary" rather than disabling
 * automatic credit reconciliation. Setting a later date protects older legacy
 * lessons from consuming newly-added credits.
 */
export const studentPaymentTracking = pgTable(
  "student_payment_tracking",
  {
    studentId: varchar("student_id")
      .primaryKey()
      .references(() => students.id, { onDelete: "cascade" }),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "restrict" }),
    authoritativeFrom: date("authoritative_from"),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    updatedByUserId: varchar("updated_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
  },
  (t) => [index("student_payment_tracking_organisation_idx").on(t.organisationId)],
);

/** Per-teacher boundary; no row means inherit the student's date. */
export const studentTeacherPaymentTracking = pgTable(
  "student_teacher_payment_tracking",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    organisationId: varchar("organisation_id").notNull().references(() => organisations.id, { onDelete: "restrict" }),
    studentId: varchar("student_id").notNull().references(() => students.id, { onDelete: "cascade" }),
    tutorProfileId: varchar("tutor_profile_id").notNull().references(() => tutorProfiles.id, { onDelete: "restrict" }),
    authoritativeFrom: date("authoritative_from").notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    updatedByUserId: varchar("updated_by_user_id").references(() => users.id, { onDelete: "set null" }),
  },
  (t) => [
    uniqueIndex("student_teacher_payment_tracking_student_tutor_idx")
      .on(t.organisationId, t.studentId, t.tutorProfileId),
    index("student_teacher_payment_tracking_organisation_idx").on(t.organisationId),
  ],
);

export interface StudentPaymentTrackingRead {
  payment_tracking_authoritative_from: string | null;
}
